import { apiClientV2 } from "@/services/api/apiClient"
import { useQuery } from "@tanstack/react-query"
import type { DataGapsStats } from "./types/dataGaps"

export const dataGapsApiCalls = {
  /** GET /material-compliance/master-data/data-gaps — the "Datenlücken" tile
   * lines. Each count is computed backend-side by the same predicate its
   * list-filter endpoint uses (TUR-1539 D5); this client only follows
   * listUrl, it never builds a filter URL itself. */
  getDataGaps: async (): Promise<DataGapsStats> => {
    const { data } = await apiClientV2.get(
      "/material-compliance/master-data/data-gaps",
    )
    return data as DataGapsStats
  },
}

/**
 * `enabled` gates the request itself: the endpoint sits behind
 * MC_MASTER_DATA_VIEW, and the nav mounts on every MC route, so an ungated
 * query would 403 for every member without that permission on every page.
 *
 * It is also part of the key, same reasoning as useMasterDataStats: a
 * disabled query must not serve the lines it cached while the permission was
 * still there. Invalidations use the three-part prefix and still match.
 */
export function useDataGapsStats({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["mc", "master-data", "data-gaps", { enabled }],
    queryFn: dataGapsApiCalls.getDataGaps,
    enabled,
    // Same as useConformityCounts, the other counts tile: a cheap aggregate
    // that every remount would otherwise refetch. Repairs still land live,
    // they invalidate this key (see invalidateMcEntityCaches).
    staleTime: 30 * 1000,
  })
}
